import { tokenChar } from "@/domain/tokens";
import { flattenRawTable, parseRaw, valueAlts } from "@/games/sc2/parser/index";
import { charToCode, qwerty } from "@/hardware/keyboard-layouts/index";

/** What aligning two variant files of one Profile recovers: a voted table plus the noise to triage. */
export interface VariantAlignment {
  /** Recovered A-slot to B-value, majority vote where several Commands agreed on a slot. */
  map: Record<string, string>;
  /** A-slot to every value voted when Commands disagreed (ranked, the winning majority first). */
  conflicts: Record<string, string[]>;
  /** Entry ids abandoned for an alternative-count or (under "skip-entry") a modifier-set mismatch. */
  skipped: string[];
}

/**
 * Resolve a char-emitting key token to its physical code through qwerty (the canonical layout every
 * TheCore file serializes): the char the token denotes, then that char's code. Null for a token that
 * denotes no char (a positional or mouse token). The shared char-key half of both alignment resolvers
 * - derive-layout's canonical side (which drops positionals) and the hand mirror's slot (which keeps
 * them as their own slot) each wrap this with their own positional policy.
 */
export function charKeyCode(key: string): string | null {
  const ch = tokenChar(key);
  return ch == null ? null : charToCode(qwerty, ch);
}

/**
 * How a per-alternative modifier-set mismatch within an aligned entry is handled. "skip-entry"
 * abandons the whole entry into `skipped` so no partial votes leak (the layout recoveries: a partial
 * mismatch means the pairing is suspect). "skip-alternative" drops only the offending alternative and
 * still counts its siblings' votes (the hand mirror: a per-hand modifier tweak on one alternative is
 * not a reason to discard a clean positional reflection on another).
 */
export type MismatchPolicy = "skip-entry" | "skip-alternative";

/**
 * Align two variant files of one Profile Command by Command and recover an unknown table by majority
 * vote. Both files serialize the same positional Profile, so each paired binding names the same
 * Command on each side: `resolveA` reads the slot the A side names (a physical code, or a raw token),
 * `resolveB` the value the B side emits there (a char, a token, or the mirrored slot). A resolver
 * returning null drops just that alternative (a positional token names no char-slot, say). Comma
 * alternatives pair by index; an alternative-count mismatch always abandons the whole entry, and a
 * modifier-set mismatch abandons it or drops the alternative per `onMismatch`. The two derive-*
 * recoveries and the hand mirror are each this same alignment, differing only in their resolvers and
 * mismatch policy.
 */
export function alignVariants(
  textA: string,
  textB: string,
  resolveA: (key: string) => string | null,
  resolveB: (key: string) => string | null,
  { onMismatch = "skip-entry" }: { onMismatch?: MismatchPolicy } = {},
): VariantAlignment {
  const a = flattenRawTable(parseRaw(textA));
  const b = flattenRawTable(parseRaw(textB));
  // Map preserves insertion order for all keys; plain objects reorder integer-like keys
  // (e.g. "9", "5") to numeric order, which breaks the first-seen tie-break for digits.
  const votes = new Map<string, Map<string, number>>();
  const skipped: string[] = [];
  for (const [id, aValue] of Object.entries(a)) {
    const bValue = b[id];
    if (bValue == null) continue;
    const aAlts = valueAlts(aValue);
    const bAlts = valueAlts(bValue);
    if (aAlts.length !== bAlts.length) {
      skipped.push(id);
      continue;
    }
    // collect first, commit after: under "skip-entry" a modifier mismatch in any alternative abandons
    // the whole entry so partial votes never leak into the map
    const entryVotes: { slot: string; value: string }[] = [];
    let mismatch = false;
    for (let i = 0; i < aAlts.length; i++) {
      const ca = aAlts[i];
      const cb = bAlts[i];
      if (!ca || !cb) continue;
      if (ca.modsKey !== cb.modsKey) {
        if (onMismatch === "skip-entry") {
          mismatch = true;
          break;
        }
        continue; // skip-alternative: drop just this alternative; its siblings still vote
      }
      const slot = resolveA(ca.key);
      if (slot == null) continue;
      const value = resolveB(cb.key);
      if (value == null) continue;
      entryVotes.push({ slot, value });
    }
    if (mismatch) {
      skipped.push(id);
      continue;
    }
    for (const v of entryVotes) {
      const perSlot = votes.get(v.slot) ?? new Map<string, number>();
      perSlot.set(v.value, (perSlot.get(v.value) ?? 0) + 1);
      votes.set(v.slot, perSlot);
    }
  }
  const map: Record<string, string> = {};
  const conflicts: Record<string, string[]> = {};
  for (const [slot, perSlot] of votes) {
    const ranked = [...perSlot.entries()].sort((x, y) => y[1] - x[1]);
    if (ranked[0]) map[slot] = ranked[0][0];
    if (ranked.length > 1) conflicts[slot] = ranked.map(([v]) => v);
  }
  return { map, conflicts, skipped };
}
